import React, { useEffect, useRef } from "react"
import { BarChart3, Users, Ticket, Star } from "lucide-react"
import { useLang } from "./i18n/LangContext"

const ICONS = [BarChart3, Users, Ticket, Star]

function FeatureCard({ icon: Icon, tagline, title, description, delay }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("opacity-100", "translate-y-0") },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className="group opacity-0 translate-y-6 transition-all duration-500 ease-out rounded-2xl bg-white dark:bg-gray-800 p-6 ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-indigo-400 dark:hover:ring-indigo-500 hover:shadow-lg hover:shadow-indigo-50 dark:hover:shadow-none"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="inline-flex items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950 p-3 mb-4 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900 transition-colors">
        <Icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 dark:text-indigo-400 mb-1">{tagline}</p>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
    </div>
  )
}

export function FeaturesSection() {
  const { t } = useLang()

  return (
    <section className="bg-gray-50 dark:bg-gray-900 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white text-balance">
            {t.features.heading}
          </h2>
          <p className="mt-4 text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-pretty">
            {t.features.subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {t.features.items.map((item, i) => (
            <FeatureCard key={i} icon={ICONS[i]} {...item} delay={i * 100} />
          ))}
        </div>
      </div>
    </section>
  )
}
